import { HeaderPanel } from '../../HeaderPanel';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { headerPanelOptions } from './panelSettings';
import { ValidationIssue, BreakLevel } from '@decisions/server/DecisionsFramework';
import classNames from 'classnames';
import './ValidationPanel.less';
import { ICancelablePromise, makeCancelable } from 'utils/PromiseUtils';
import { getValidationIssuesForEntity } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/EntityDetails/EntityDetailsService';
import { Spinner } from 'components/Spinner';

interface ValidationPanelProps {
  validationIssues?: ValidationIssue[];
  loadValidationIssues?: boolean;
  entityId: string;
  typeName: string;
}

function getValidationIconClasses(breackLevel: BreakLevel) {
  return classNames(`dp-entitydetails-validation__icon`, {
    [`dp-entitydetails-validation__icon--warning`]: breackLevel === BreakLevel.Warning,
    [`dp-entitydetails-validation__icon--fatal`]: breackLevel === BreakLevel.Fatal,
  });
}

export function ValidationPanel({ loadValidationIssues, entityId, typeName }: ValidationPanelProps) {
  const [validations, setValidations] = useState<ValidationIssue[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const loadIssuesRef = useRef<ICancelablePromise<ValidationIssue[]> | null>(null);

  const loadIssues = useCallback(async () => {
    try {
      setIsLoading(true);

      loadIssuesRef.current = makeCancelable(getValidationIssuesForEntity({ entityId, typeName }));      
      setValidations(await loadIssuesRef.current.promise);      
    } finally {
      setIsLoading(false);
    }
  }, [entityId, typeName]);

  useEffect(() => {
    loadIssues();
    return () => {
      loadIssuesRef?.current?.cancel?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showLoadingInfo = loadValidationIssues && loadIssuesRef.current && !validations?.length;
  return (
    <HeaderPanel {...headerPanelOptions('primary', 'Validation')}>
      <ul>
        {validations?.map((issue, index) => (
          <li key={index} className="dp-entitydetails-validation">
            <div className={getValidationIconClasses(issue.Level)}></div>
            <div className={`dp-entitydetails-validation__text`} title={issue.Reason}>
              {issue.Reason}
            </div>
          </li>
        ))}
        {showLoadingInfo && (
          <div className="dp-entitydetails-validation__loading-info">
            {isLoading && <Spinner size={20} />}
            <span className="dp-entitydetails-validation__loading-text">{isLoading ? 'Loading...' : 'No Issues'}</span>
          </div>
        )}
      </ul>
    </HeaderPanel>
  );
}
